import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCart, cartLineKey } from "@/context/CartContext";
import Navbar from "@/components/Navbar";
import { US_STATES } from "@/lib/constants";
import { toast } from "@/lib/toast";
import { Loader2, ShoppingBag, Minus, Plus, ChevronLeft, Trash2, Shirt } from "lucide-react";

const fmtPrice = (c) =>
  c == null ? "—" : `$${(c / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const inputCls = "w-full bg-input border border-sniper focus:border-olive outline-none px-3 py-2.5 text-foreground transition-colors font-body text-sm";
const labelCls = "font-mono text-[10px] uppercase tracking-wide text-secondary-foreground block mb-1";

export default function SwagCart() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const cart = useCart();
  const [params, setParams] = useSearchParams();
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    ship_name: "", ship_email: "", ship_address1: "", ship_address2: "",
    ship_city: "", ship_state: "", ship_zip: "",
  });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // A canceled Stripe checkout returns here.
  useEffect(() => {
    if (params.get("order") === "cancel") { toast("Checkout canceled."); setParams({}, { replace: true }); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  // Prefill shipping from the signed-in profile (only empty fields, so typed values survive).
  useEffect(() => {
    if (!user || typeof user !== "object") return;
    setForm((f) => ({
      ...f,
      ship_name: f.ship_name || (user.name || "").trim(),
      ship_email: f.ship_email || user.email || "",
      ship_city: f.ship_city || user.city || "",
      ship_state: f.ship_state || user.state || "",
      ship_zip: f.ship_zip || user.zip_code || "",
    }));
  }, [user]);

  const lines = cart?.lines || [];
  const empty = lines.length === 0;
  const anyPreorder = lines.some((l) => l.preorder);

  const checkout = async () => {
    if (!user || typeof user !== "object") { toast.error("Sign in to check out."); navigate("/join"); return; }
    if (empty) { toast.error("Your cart is empty."); return; }
    for (const [k, label] of [["ship_name", "name"], ["ship_email", "email"], ["ship_address1", "street address"], ["ship_city", "city"], ["ship_state", "state"], ["ship_zip", "ZIP"]]) {
      if (!String(form[k] || "").trim()) { toast.error(`Enter a shipping ${label}.`); return; }
    }
    setBusy(true);
    try {
      const { data } = await api.post("/swag/cart/checkout", {
        items: lines.map((l) => ({
          item_id: l.item_id,
          quantity: l.quantity,
          ...(l.size ? { size: l.size } : {}),
          ...(l.color ? { color: l.color } : {}),
        })),
        origin_url: window.location.origin,
        ...form,
      });
      if (data.url) { window.location.href = data.url; return; }
      toast.error("Could not start checkout. Please try again.");
      setBusy(false);
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      toast.error(status === 503 ? "Payments are temporarily unavailable — please try again shortly." : (formatError(detail) || "Checkout is unavailable right now."));
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen text-foreground">
      <Navbar />
      <main className="max-w-[1100px] mx-auto px-4 pt-8 pb-24" data-testid="swag-cart-page">
        <Link to="/swag" data-testid="swag-cart-back" className="inline-flex items-center gap-1 font-mono text-[11px] uppercase tracking-wide text-secondary-foreground hover:text-olive-bright transition-colors mb-8">
          <ChevronLeft className="w-3.5 h-3.5" /> Keep shopping
        </Link>

        <h1 className="font-heading uppercase tracking-tight text-3xl sm:text-4xl text-foreground mb-8" data-testid="swag-cart-title">Your cart</h1>

        {empty ? (
          <div className="py-24 flex flex-col items-center justify-center text-center" data-testid="swag-cart-empty">
            <ShoppingBag className="w-9 h-9 text-muted-foreground mb-4" />
            <p className="font-mono uppercase tracking-[0.14em] text-secondary-foreground text-base">Your cart is empty.</p>
            <Link to="/swag" className="mt-5 font-mono text-[11px] uppercase tracking-wide text-olive-bright hover:underline">← Browse Swag</Link>
          </div>
        ) : (
          <div className="grid lg:grid-cols-[1.5fr,1fr] gap-8 lg:gap-12 items-start">
            {/* Line items */}
            <div className="space-y-3" data-testid="swag-cart-lines">
              {lines.map((l) => {
                const k = cartLineKey(l);
                const variant = [l.color, l.size].filter(Boolean).join(" · ");
                return (
                  <div key={k} className="flex gap-4 border border-sniper bg-surface p-3" data-testid={`swag-cart-line-${k}`}>
                    <div className="w-20 h-20 shrink-0 bg-obsidian border border-sniper flex items-center justify-center overflow-hidden">
                      {l.image ? <img src={l.image} alt={l.item_name} className="w-full h-full object-contain" /> : <Shirt className="w-6 h-6 text-muted-foreground" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="font-heading uppercase tracking-tight text-foreground text-base leading-tight truncate">{l.item_name}</p>
                          {variant && <p className="font-mono text-[11px] uppercase text-secondary-foreground mt-0.5" data-testid={`swag-cart-variant-${k}`}>{variant}</p>}
                          {l.preorder && <span className="inline-block mt-1 font-mono text-[9px] uppercase tracking-wide border border-[#d3a24e]/70 text-[#d3a24e] px-1.5 py-0.5" data-testid={`swag-cart-preorder-${k}`}>Pre-order</span>}
                        </div>
                        <button onClick={() => cart.remove(k)} data-testid={`swag-cart-remove-${k}`} title="Remove" className="shrink-0 text-muted-foreground hover:text-blaze transition-colors p-1">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="flex items-end justify-between gap-3 mt-2">
                        <div className="flex items-center border border-sniper">
                          <button onClick={() => cart.setQty(k, l.quantity - 1)} className="px-2.5 py-1.5 text-foreground hover:bg-olive/10" data-testid={`swag-cart-qty-dec-${k}`}><Minus className="w-3 h-3" /></button>
                          <span className="px-3 font-mono text-sm tabular-nums text-foreground" data-testid={`swag-cart-qty-${k}`}>{l.quantity}</span>
                          <button onClick={() => cart.setQty(k, l.quantity + 1)} className="px-2.5 py-1.5 text-foreground hover:bg-olive/10" data-testid={`swag-cart-qty-inc-${k}`}><Plus className="w-3 h-3" /></button>
                        </div>
                        <span className="font-heading text-olive-bright text-base" data-testid={`swag-cart-line-total-${k}`}>{fmtPrice((l.unit_price_cents || 0) * l.quantity)}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Summary + shipping + checkout */}
            <div className="border border-sniper bg-surface p-5 space-y-4">
              <div className="flex items-center justify-between">
                <span className="font-mono text-[11px] uppercase tracking-wide text-secondary-foreground">Subtotal</span>
                <span className="font-heading text-xl text-olive-bright" data-testid="swag-cart-subtotal">{fmtPrice(cart.subtotalCents)}</span>
              </div>
              {anyPreorder && (
                <p className="font-mono text-[10px] text-[#d3a24e] leading-relaxed" data-testid="swag-cart-preorder-note">
                  Cart includes pre-order items — charged today, they ship when available.
                </p>
              )}

              {!user || typeof user !== "object" ? (
                <div className="border-t border-sniper pt-4">
                  <p className="font-body text-sm text-secondary-foreground mb-3">Sign in to check out.</p>
                  <Link to="/join" data-testid="swag-cart-signin-btn" className="inline-flex items-center justify-center gap-2 bg-olive hover:bg-olive-bright text-obsidian font-mono uppercase text-xs tracking-wide px-4 py-3 transition-colors w-full">Sign in to check out</Link>
                </div>
              ) : (
                <>
                  <div className="border-t border-sniper pt-4 space-y-3">
                    <div className="font-mono text-[11px] uppercase tracking-wide text-olive-bright">Ship to</div>
                    <div className="grid sm:grid-cols-2 gap-3">
                      <div><label className={labelCls}>Full name</label><input value={form.ship_name} onChange={set("ship_name")} className={inputCls} data-testid="swag-cart-ship-name" /></div>
                      <div><label className={labelCls}>Email</label><input type="email" value={form.ship_email} onChange={set("ship_email")} className={inputCls} data-testid="swag-cart-ship-email" /></div>
                    </div>
                    <div><label className={labelCls}>Street address</label><input value={form.ship_address1} onChange={set("ship_address1")} className={inputCls} data-testid="swag-cart-ship-address1" /></div>
                    <div><label className={labelCls}>Apt / suite (optional)</label><input value={form.ship_address2} onChange={set("ship_address2")} className={inputCls} data-testid="swag-cart-ship-address2" /></div>
                    <div className="grid grid-cols-3 gap-3">
                      <div><label className={labelCls}>City</label><input value={form.ship_city} onChange={set("ship_city")} className={inputCls} data-testid="swag-cart-ship-city" /></div>
                      <div>
                        <label className={labelCls}>State</label>
                        <select value={form.ship_state} onChange={set("ship_state")} className={inputCls} data-testid="swag-cart-ship-state">
                          <option value="">—</option>
                          {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>
                      <div><label className={labelCls}>ZIP</label><input value={form.ship_zip} onChange={set("ship_zip")} className={inputCls} data-testid="swag-cart-ship-zip" /></div>
                    </div>
                  </div>
                  <button onClick={checkout} disabled={busy} data-testid="swag-cart-checkout"
                    className="w-full bg-olive hover:bg-olive-bright disabled:opacity-50 disabled:cursor-not-allowed text-obsidian font-mono uppercase text-xs tracking-wide px-4 py-3 transition-colors inline-flex items-center justify-center gap-2">
                    {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting…</> : <><ShoppingBag className="w-4 h-4" /> Continue to payment</>}
                  </button>
                  <p className="font-mono text-[10px] text-center text-muted-foreground">Secure checkout via Stripe. First-party gear shipped by OpticSwap.</p>
                </>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
